import { Button } from '@/components/ui/button';
import { useNamespaceCopy } from '@/i18n/ns';
import appDataCopy from '@/i18n/ns/app-data';
import type { RenderImage } from '../media/media-parts';
import type { RestorePoint } from '../lifecycle/restore-dialogs';
import type {
  DataSource,
  DataTable,
  KeptHistory,
  PendingRemoval,
  RowChange,
  RowHistoryState,
  RowsState,
} from './data-model';
import {
  KeptHistoryList,
  PendingRemovals,
  RestorePointsList,
  RowHistoryPopover,
  RowsTable,
  SourcesList,
  TablesList,
} from './data-parts';

export type DataTabProps = {
  tables: readonly DataTable[];
  /** Each table's own rows state, by table id. */
  rows: Readonly<Record<string, RowsState>>;
  /** The open row history, if any. */
  history: RowHistoryState | null;
  /** Absent: not known for this Data, so the section is not drawn (never an empty list that claims "none"). */
  restorePoints?: readonly RestorePoint[];
  retentionDays?: number;
  /** Absent: the Data read carries no source facts, so no "reads no company systems" claim is made. */
  sources?: readonly DataSource[];
  kept: readonly KeptHistory[];
  pending: readonly PendingRemoval[];
  /** Rights: editors export and review; owners restore and clear; anyone with Undo rights undoes a row. */
  canExport: boolean;
  canRestore: boolean;
  canReset: boolean;
  canUndoRow: boolean;
  undoing: string | null;
  formatAt: (iso: string) => string;
  /** The list of tables itself is still being read, or could not be: said so, never an empty list. */
  listing?: 'loading' | 'failed' | 'unavailable';
  /** APP's shared image primitive (managed refs, five states, zoom); absent: images are not drawn. */
  renderImage?: RenderImage;
  on: {
    onExport: (tableId: string) => void;
    onRestoreToTime: (tableId: string) => void;
    onViewFiles: (tableId: string) => void;
    onRowHistory?: (tableId: string, rowId: string) => void;
    onCloseHistory: () => void;
    onUndo: (change: RowChange) => void;
    onMore: (tableId: string) => void;
    onRetry: (tableId: string) => void;
    onRestorePoint: (id: string) => void;
    onManageSource: (id: string) => void;
    onReviewRemoval: (id: string) => void;
    onReset: () => void;
  };
};

/** The Data tab: its parts in the order DES1 draws them. State and rights come from the caller. */
export function DataTab(props: DataTabProps) {
  const copy = useNamespaceCopy(appDataCopy);
  if (props.listing === 'loading')
    return (
      <div className="pc-data-tab" data-testid="data-tab">
        <p data-testid="rows-loading" aria-busy="true">
          {copy.loading}
        </p>
      </div>
    );
  if (props.listing === 'failed')
    return (
      <div className="pc-data-tab" data-testid="data-tab">
        <div role="alert" data-testid="rows-failed" className="pc-data-failed">
          <p>{copy.rowsFailed}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => props.on.onRetry('')}>
            {copy.retry}
          </Button>
        </div>
      </div>
    );
  if (props.listing === 'unavailable')
    return (
      <div className="pc-data-tab" data-testid="data-tab">
        <p role="status" data-testid="data-unavailable">
          {copy.rowsFailed}
        </p>
      </div>
    );
  return (
    <div className="pc-data-tab" data-testid="data-tab">
      {props.tables.length === 0 && (
        <p role="status" data-testid="data-empty">
          {copy.noEntries}
        </p>
      )}
      <TablesList
        tables={props.tables}
        formatAt={props.formatAt}
        canExport={props.canExport}
        canRestore={props.canRestore}
        onExport={props.on.onExport}
        onRestoreToTime={props.on.onRestoreToTime}
        onViewFiles={props.on.onViewFiles}
        renderRows={(table) => (
          <RowsTable
            state={props.rows[table.id] ?? { status: 'loading' }}
            onRowHistory={
              props.on.onRowHistory
                ? (rowId) => props.on.onRowHistory?.(table.id, rowId)
                : undefined
            }
            onMore={() => props.on.onMore(table.id)}
            onRetry={() => props.on.onRetry(table.id)}
            renderImage={props.renderImage}
            ownerScope={table.id}
          />
        )}
      />
      <PendingRemovals items={props.pending} onReview={props.on.onReviewRemoval} />
      {props.restorePoints && props.retentionDays !== undefined && (
        <RestorePointsList
          points={props.restorePoints}
          retentionDays={props.retentionDays}
          canRestore={props.canRestore}
          onRestore={props.on.onRestorePoint}
        />
      )}
      {props.sources && (
        <SourcesList
          sources={props.sources}
          formatAt={props.formatAt}
          onManage={props.on.onManageSource}
        />
      )}
      <KeptHistoryList items={props.kept} />
      {props.canReset && (
        <div className="pc-data-reset">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="data-reset"
            onClick={() => props.on.onReset()}
          >
            {copy.clearEntries}
          </Button>
        </div>
      )}
      {props.history && (
        <RowHistoryPopover
          state={props.history}
          formatAt={props.formatAt}
          canUndoRow={props.canUndoRow}
          undoing={props.undoing}
          onUndo={props.on.onUndo}
          onClose={props.on.onCloseHistory}
          renderImage={props.renderImage}
        />
      )}
    </div>
  );
}
